import React, { useState } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const SECTORS = ["All Sectors", "Energy", "Transport", "Digital Infrastructure", "Water"];
const REGIONS = ["All Regions", "UK", "Europe", "North America", "Asia Pacific", "Africa"];
const STAGES = ["All Stages", "Sourcing", "Screening", "Diligence", "IC Review", "Execution", "Asset Management"];
const RISK_RATINGS = ["All", "Low", "Medium", "High"];
const IMPACT_THEMES = ["Climate", "Social Infrastructure", "Digital Access", "Water Security", "Clean Energy", "Mobility"];

export default function MobileFilterPanel({ isOpen, onClose, filters, onFilterChange }) {
  const [localFilters, setLocalFilters] = useState(filters);
  const [activeImpact, setActiveImpact] = useState(filters?.impactThemes || []);

  const handleImpactToggle = (theme) => {
    const updated = activeImpact?.includes(theme)
      ? activeImpact?.filter((t) => t !== theme)
      : [...activeImpact, theme];
    setActiveImpact(updated);
    setLocalFilters((prev) => ({ ...prev, impactThemes: updated }));
  };

  const handleApply = () => {
    onFilterChange({ ...localFilters, impactThemes: activeImpact });
    onClose();
  };

  const handleReset = () => {
    const reset = { sector: "All Sectors", region: "All Regions", stage: "All Stages", riskRating: "All", impactThemes: [], irrMin: 0, irrMax: 30, search: "" };
    setLocalFilters(reset);
    setActiveImpact([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-sm bg-card h-full overflow-y-auto flex flex-col shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-heading font-semibold text-foreground text-lg">Filters</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 p-4 space-y-5">
          {[
            { label: "Sector", key: "sector", options: SECTORS },
            { label: "Region", key: "region", options: REGIONS },
            { label: "Stage", key: "stage", options: STAGES },
            { label: "Risk Rating", key: "riskRating", options: RISK_RATINGS },
          ]?.map(({ label, key, options }) => (
            <div key={key}>
              <label className="block text-sm font-caption font-medium text-foreground mb-2">{label}</label>
              <select
                value={localFilters?.[key]}
                onChange={(e) => setLocalFilters((prev) => ({ ...prev, [key]: e?.target?.value }))}
                className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2.5 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              >
                {options?.map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
          ))}

          <div>
            <label className="block text-sm font-caption font-medium text-foreground mb-2">
              Target IRR: {localFilters?.irrMin}% – {localFilters?.irrMax}%
            </label>
            <div className="space-y-2">
              <input type="range" min={0} max={30} step={1} value={localFilters?.irrMin}
                onChange={(e) => setLocalFilters((prev) => ({ ...prev, irrMin: Number(e?.target?.value) }))}
                className="w-full accent-primary" />
              <input type="range" min={0} max={30} step={1} value={localFilters?.irrMax}
                onChange={(e) => setLocalFilters((prev) => ({ ...prev, irrMax: Number(e?.target?.value) }))}
                className="w-full accent-primary" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-caption font-medium text-foreground mb-2">Impact Themes</label>
            <div className="flex flex-wrap gap-2">
              {IMPACT_THEMES?.map((theme) => (
                <button
                  key={theme}
                  onClick={() => handleImpactToggle(theme)}
                  className={`px-3 py-1.5 rounded-full text-xs font-caption font-medium border transition-all ${
                    activeImpact?.includes(theme)
                      ? "border-primary bg-primary text-white" :"border-border bg-background text-muted-foreground"
                  }`}
                >
                  {theme}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-border flex gap-3">
          <Button variant="outline" fullWidth onClick={handleReset}>Reset</Button>
          <Button variant="default" fullWidth onClick={handleApply}>Apply Filters</Button>
        </div>
      </div>
    </div>
  );
}